const FALLBACK_LOCALE_KEY = 'en_US';

export function getLocalizedValue(value: string | { [locale: string]: string } | null | undefined, locale: string): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;

  const keys = Object.keys(value);
  if (keys.length === 0) return '';

  const normalize = (s: string) => s.replace(/_/g, '-').toLowerCase();
  const target = normalize(locale);
  const match = keys.find(k => normalize(k) === target);
  if (match) return value[match];

  if (value[FALLBACK_LOCALE_KEY]) return value[FALLBACK_LOCALE_KEY];
  const englishKey = keys.find(k => normalize(k).startsWith('en-'));
  if (englishKey) return value[englishKey];

  return value[keys[0]];
}

export const CHART_COLORS = ['#1F91BF', '#F56B16', '#00A879', '#E8384F', '#AA71FF', '#829D15', '#EA4E9D', '#AF9200', '#DB57FF', '#5E92F5', '#53A199',];

// djb2 hash used so same names land on the same colors always
const getColorIndex = (name: string): number => {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = Math.imul(hash, 33) ^ name.charCodeAt(i);
  }

  // shift to unsigned to avoid negative numbers
  return (hash >>> 0) % CHART_COLORS.length;
};

export const getColor = (name: string): string => CHART_COLORS[getColorIndex(name)];

// darker shade of a palette color, used to highlight the bar under the cursor
export const getHoverColor = (color: string, amount = 0.15): string => {
  const hex = color.replace('#', '');
  const channels = [0, 2, 4].map(i => {
    const value = parseInt(hex.slice(i, i + 2), 16);
    return Math.round(value * (1 - amount))
      .toString(16)
      .padStart(2, '0');
  });

  return `#${channels.join('')}`;
};

// assign colors to each name avoiding collisions and preferably keeping the same color for the same name
export const assignColors = (names: string[]): Map<string, string> => {
  const unique = [...new Set(names)].sort();
  const result = new Map<string, string>();
  const used = new Set<string>();

  for (const name of unique) {
    let index = getColorIndex(name);

    // two names can hash to the same slot, so walk forward until we find a free color
    for (let i = 0; i < CHART_COLORS.length && used.has(CHART_COLORS[index]); i++) {
      index = (index + 1) % CHART_COLORS.length;
    }

    const color = CHART_COLORS[index];
    result.set(name, color);
    used.add(color);
  }

  return result;
};